/**
 * RTWO Cryptography & Identity Engine — Signature Verification Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type {
  ISignatureVerificationService,
  SignatureVerificationRequest,
  SignatureVerificationResult,
} from '../service-interfaces';
import type { KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class SignatureVerificationService
  extends BaseCryptoService
  implements ISignatureVerificationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.SIGNATURE_VERIFICATION;
  readonly serviceName = 'Signature Verification Service';
  readonly description = 'Verifies cryptographic signatures using standard provider abstractions.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R4 Blockchain Protocol Engine', 'R6 Security & Automation'];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly keyProviderRegistry: KeyProviderRegistry
  ) {
    super(context);
  }

  async verifySignature(
    request: SignatureVerificationRequest
  ): Promise<SignatureVerificationResult> {
    const provider = this.keyProviderRegistry.getActiveProvider();
    if (!this.keyProviderRegistry.isConfigured() || !provider.isReady()) {
      return {
        valid: false,
        error: 'Signature verification unavailable. Key Provider not configured or not connected.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const valid = await provider.verifySignature(
      request.messageHash,
      request.signature,
      request.publicKey,
      request.algorithm
    );

    this.context.eventBus.emit(CryptoEventTypes.SIGNATURE_VERIFIED, request.publicKey, {
      messageHash: request.messageHash,
      valid,
    });

    return {
      valid,
      error: valid ? null : 'Signature verification failed.',
      source: valid ? 'Key Provider — Signature Verified' : 'Key Provider — Verification Failed',
    };
  }
}
